/*
 * NOTE
 * The shelter's chosen date format, held outside the session store on purpose.
 *
 * `helpers/RULES.md` says helpers are pure — no store access — so `date.helper.ts`
 * can never import the session store. Instead the session store WRITES the format
 * here (`setActiveDateFormat`) and the helper READS it. No helper gains a store
 * dependency.
 *
 * It is a `ref` so first login and shelter switching (`AActionLoginAs`) re-render
 * dates without a manual reload. It is seeded SYNCHRONOUSLY at module load from the
 * persisted `shelterProfile` in localStorage, so a hard refresh never flashes the
 * default format before the profile refetch resolves.
 *
 * Blind spot: a `ref` only heals dates formatted inside reactive contexts. Dates
 * formatted imperatively into plain row arrays inside `.then` handlers do not
 * re-render on a shelter switch — those sites need a refetch.
 * */

import { ref } from 'vue';
import type { PhpDateFormat } from './dateFormats';
import {
  DEFAULT_PHP_DATE_FORMAT,
  PHP_DATE_FORMATS,
  PHP_TO_DATE_FNS_DATE_FORMAT,
  PHP_TO_DATE_FNS_DAY_MONTH_FORMAT,
  PHP_TO_MOMENT_DATE_FORMAT,
  PHP_TO_MOMENT_DAY_MONTH_FORMAT,
  PHP_TO_PRIMEVUE_DATE_FORMAT,
  PHP_TO_PRIMEVUE_MONTH_YEAR_FORMAT,
} from './dateFormats';
import { readPersistedShelterProfile } from './shelterProfileStorage';

const activeDateFormat = ref<PhpDateFormat>(getPersistedDateFormat());

// HELPERS
function isSupportedDateFormat(format: unknown): format is PhpDateFormat {
  const isString = typeof format === 'string';

  return isString && PHP_DATE_FORMATS.includes(format as PhpDateFormat);
}

function getPersistedDateFormat(): PhpDateFormat {
  const shelterProfile = readPersistedShelterProfile();
  const storedFormat = shelterProfile?.date_format;

  return isSupportedDateFormat(storedFormat) ? storedFormat : DEFAULT_PHP_DATE_FORMAT;
}

function getActiveMomentDateFormat(): string {
  return PHP_TO_MOMENT_DATE_FORMAT[activeDateFormat.value];
}

function getActivePrimeVueDateFormat(): string {
  return PHP_TO_PRIMEVUE_DATE_FORMAT[activeDateFormat.value];
}

function getActiveDateFnsDateFormat(): string {
  return PHP_TO_DATE_FNS_DATE_FORMAT[activeDateFormat.value];
}

function getActiveMomentDayMonthFormat(): string {
  return PHP_TO_MOMENT_DAY_MONTH_FORMAT[activeDateFormat.value];
}

function getActiveDateFnsDayMonthFormat(): string {
  return PHP_TO_DATE_FNS_DAY_MONTH_FORMAT[activeDateFormat.value];
}

function getActivePrimeVueMonthYearFormat(): string {
  return PHP_TO_PRIMEVUE_MONTH_YEAR_FORMAT[activeDateFormat.value];
}

// EVENTS
function setActiveDateFormat(format: unknown) {
  activeDateFormat.value = isSupportedDateFormat(format) ? format : DEFAULT_PHP_DATE_FORMAT;
}

function resetActiveDateFormat() {
  activeDateFormat.value = DEFAULT_PHP_DATE_FORMAT;
}

export {
  getActiveDateFnsDateFormat,
  getActiveDateFnsDayMonthFormat,
  getActiveMomentDateFormat,
  getActiveMomentDayMonthFormat,
  getActivePrimeVueDateFormat,
  getActivePrimeVueMonthYearFormat,
  resetActiveDateFormat,
  setActiveDateFormat,
};
